import { removeAllToasts } from '@medly-components/core';
import { useRegionContext } from 'context/RegionContext';
import { UserContext } from 'context/UserContext';
import { ZoneContext } from 'context/ZoneContext';
import { useMantaCareAPI } from 'hooks/useMantaCareAPI/useMantaCareAPI';
import { PathSelectionOptionValueType, Position } from 'pages/Map/types';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useOutsideAlerter } from 'utils/utils';
import { ReactComponent as PathNotTraveledSelected } from '../../../../../../../../assets/svg/paths/not_traveled_selected.svg';
import { ReactComponent as PathNotTraveledUnSelected } from '../../../../../../../../assets/svg/paths/not_traveled_unselected.svg';
import { ReactComponent as ResetPathSelected } from '../../../../../../../../assets/svg/paths/reset_selected.svg';
import { ReactComponent as ResetPathUnSelected } from '../../../../../../../../assets/svg/paths/reset_unselected.svg';
import { ReactComponent as PathTraveledSelected } from '../../../../../../../../assets/svg/paths/traveled_selected.svg';
import { ReactComponent as PathTraveledUnSelected } from '../../../../../../../../assets/svg/paths/traveled_unselected.svg';
import { Loader, PathSelectionContent, PathSelectionModal } from './PathSelection.styled';
import { PathSelectionIcon } from './PathSelectionIcon';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';

type PathSelectionProps = {
    onClose?: () => void;
    mousePosition: Position;
};

export enum PathSelectionTestIDs {
    TraveledPath = 'traveled-path',
    NotTraveledPath = 'not-traveled-path',
    ResetPath = 'reset-path'
}

/**
 * This component shows path selection options in modal
 */
export const PathSelection = ({ onClose, mousePosition }: PathSelectionProps) => {
    const optionsContainerRef = useRef(null);
    const { selectedPath, setSelectedPath, zone, scale } = useContext(ZoneContext);
    const { user } = useContext(UserContext);
    const { region, updateRegionWithPath } = useRegionContext();
    const { pathTraversal, error, loading } = useMantaCareAPI();
    const { captureMantaCareEvent } = useAnalytics();

    const close = useCallback(() => {
        setSelectedPath(undefined);
        onClose && onClose();
    }, [onClose]);

    /**
     * Triggers path traversal API for choosen path with selected option
     * Shows loader during API call
     * Shows error toast if any api failure
     * Updates region data received via api
     * Resets selected path and closes the path selection modal
     */
    const onOptionSelect = useCallback(
        async (value: PathSelectionOptionValueType) => {
            if (!user || !selectedPath) {
                return;
            }
            const regionId = zone.ref ? zone.ref.regionId : region.id;
            const response = await pathTraversal(user.id, regionId, {
                startStation: selectedPath.source.getId(),
                endStation: selectedPath.target.getId(),
                isSelected: value
            });
            if (!error && response) {
                selectedPath.setIsSelected(value);
                updateRegionWithPath(zone.name, selectedPath);

                const source = selectedPath.source;
                const target = selectedPath.target;

                captureMantaCareEvent({
                    type: 'map',
                    action: 'path_marked',
                    metadata: {
                        region: {
                            id: region.id,
                            name: region.name
                        },
                        map: { name: region.map.name },
                        zone: { name: zone.name },
                        source: {
                            id: source.id,
                            name: source.name,
                            type: source.type,
                            description: source.description
                        },
                        target: {
                            id: target.id,
                            name: target.name,
                            type: target.type,
                            description: target.description
                        },
                        description: selectedPath.description || ''
                    }
                });
            }
            close();
        },
        [selectedPath, user, pathTraversal, close]
    );

    useEffect(() => {
        return () => removeAllToasts();
    }, []);

    /**
     * We need to close modal on outside modal content click
     * But we shoudn't close on outside click while API in progress
     */
    useOutsideAlerter(optionsContainerRef, close, { exceptionEleId: 'path-selection-loader' });

    if (!selectedPath) {
        return null;
    }

    return (
        <PathSelectionModal id="path-selection-modal">
            {loading ? <Loader id="path-selection-loader" data-testid="path-selection-loader" /> : null}
            <PathSelectionContent
                onClick={e => e.stopPropagation()}
                ref={optionsContainerRef}
                scale={scale}
                style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` }}
            >
                <PathSelectionIcon
                    testId={PathSelectionTestIDs.TraveledPath}
                    path={selectedPath}
                    value={true}
                    Selected={PathTraveledSelected}
                    Unselected={PathTraveledUnSelected}
                    onClick={onOptionSelect}
                />
                <PathSelectionIcon
                    testId={PathSelectionTestIDs.NotTraveledPath}
                    path={selectedPath}
                    value={false}
                    Selected={PathNotTraveledSelected}
                    Unselected={PathNotTraveledUnSelected}
                    onClick={onOptionSelect}
                />
                <PathSelectionIcon
                    testId={PathSelectionTestIDs.ResetPath}
                    path={selectedPath}
                    value={null}
                    Selected={ResetPathSelected}
                    Unselected={ResetPathUnSelected}
                    onClick={onOptionSelect}
                />
            </PathSelectionContent>
        </PathSelectionModal>
    );
};
